import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { IonRow, IonCol, IonLabel, IonCard, IonCardContent, IonTextarea, IonText, IonChip, isPlatform } from "@ionic/react";
import EntrySubSectionCard from "./EntrySubSectionCard";
import EntryStatusChip from "./EntryStatusChip";
import { Event, EventEntry, EventRules, EventRulesAgreement } from "../../../models";
import { PersonContext } from "../../../context/PersonContext";
import { SubSectionCardsSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import { getEventRulesByEventId } from "../../../utilities/eventRules/EventRules";
import NewRuleAgreementForm from "../../EventRules/NewRuleAgreementForm";
import ErrorAlert from "../../Errors/ErrorAlert";
import { isWindows } from "../../../utilities/platform/Platform";
import { createEventRulesAgreement, deleteEventRulesAgreement, getEventRulesAgreementsByEntryId, updateEventRulesAgreement } from "../../../utilities/eventRules/EventRulesAgreement";
import { CreateEventRulesAgreementInput, DeleteEventRulesAgreementInput, UpdateEventRulesAgreementInput } from "../../../API";
import { FormattedEventRule, FormattedWaiverData, verifyRuleInput } from "../../../utilities/eventRules/WaiversUtils";
import BasicTextarea from "../../TextArea/BasicTextarea";

/**
 * Imperative API exposed via `ref`. The parent card owns the Save button and decides
 * when to collapse; this handle lets it ask the section to persist its pending edits first.
 */
export interface WaiversSectionHandle {
    /** Persist any pending waiver edits. No-op when there are no unsaved changes. */
    save: () => Promise<void>;
}

interface WaiversSectionProps {
    event: Event;
    completedEventEntries?: EventEntry[];
    waiversData?: (FormattedWaiverData[] | null | undefined);
    onItemsChange?: (updatedWaiversData: FormattedWaiverData[]) => void;
    onDirtyChange?: (isDirty: boolean) => void;
    /** Bumps when navigating from Entry Summary so each horse waiver accordion remounts with `defaultExpanded`. */
    waiverAccordionFocusKey?: number;
    /** When set, only this `EventEntry.id` waiver sub-card is expanded; others start collapsed. */
    expandWaiverOnlyForEventEntryId?: string;
}

const WaiversSection = forwardRef<WaiversSectionHandle, WaiversSectionProps>(({
    event,
    completedEventEntries,
    waiversData,
    onItemsChange,
    onDirtyChange,
    waiverAccordionFocusKey = 0,
    expandWaiverOnlyForEventEntryId,
}, ref) => {
    const user = useContext(PersonContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [formattedWaiverData, setFormattedWaiverData] = useState<FormattedWaiverData[]>([]);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    useEffect(() => {
        onDirtyChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    const hasIncompleteWaiversForEntry = (entryData: FormattedWaiverData): boolean => {
        if (!entryData.rules?.length) return false;
        return entryData.rules.some((rule) => rule.isRequired && !verifyRuleInput(rule.fullName, rule.isAgreed));
    };

    const formatEventRules = (eventRules: EventRules[], selectedPreviousAgreements?: EventRulesAgreement[]) => {
        let formattedRules: FormattedEventRule[] = [];
        for (var i = 0; i < eventRules.length; i++) {
            const currentRule = eventRules[i];
            let foundPreviousAgreement = false;
            let previousAgreement: EventRulesAgreement | null | undefined;
            if (selectedPreviousAgreements) {
                selectedPreviousAgreements.forEach((previous: EventRulesAgreement) => {
                    if (previous.eventRule?.id === currentRule.id) {
                        foundPreviousAgreement = true;
                        previousAgreement = previous;
                    }
                });
            }
            const formattedRule: FormattedEventRule = {
                isAgreed: foundPreviousAgreement,
                fullName: (previousAgreement && previousAgreement.name) ? previousAgreement.name : "",
                isRequired: currentRule.requiresAgreementPerEntry ? true : false,
                isCollapsed: true,
                rule: currentRule,
                pastRuleAgreement: previousAgreement || null
            }
            formattedRules.push(formattedRule);
        } 
        return formattedRules;
    }
 
    const getFormattedWaiverData = async (currentEventEntries: EventEntry[]) => {
        // First, get the rules for this event.
        let eventRules: EventRules[] = [];
        const rulesQueryResult = await getEventRulesByEventId(event.id);
        if (rulesQueryResult.isSuccess) {
            eventRules = rulesQueryResult.result;
        } else {
            setError("Sorry, a problem occurred while trying to find the event's rules.");
        }

        // Next, loop through each entry and format the data.
        let formattedEntries: FormattedWaiverData[] = [];
        for (let i = 0; i < currentEventEntries.length; i++) {
            const current = currentEventEntries[i];

            // Get the previous rule agreements for this entry
            let previousRuleAgreements: EventRulesAgreement[] = [];
            const agreementsQueryResult = await getEventRulesAgreementsByEntryId(current.id);
            if (agreementsQueryResult.isSuccess) {
                previousRuleAgreements = agreementsQueryResult.result;
            } else {
                setError("Sorry, a problem occurred while trying to find the past rule agreements.");
            }

            // Format the rules
            const formattedRules = formatEventRules(eventRules, previousRuleAgreements);
            
            const title = "Entry " + (i + 1).toString() + " - " + current.horseName;
            const formattedData: FormattedWaiverData = {
                id: current.id,
                title: title,
                eventEntry: current,
                rules: formattedRules || [],
            };
            formattedEntries.push(formattedData);
        }
        setFormattedWaiverData(formattedEntries);
    };

    const getDataOnLoad = async (completedEventEntries: EventEntry[]) => {
        setIsLoading(true);
        await getFormattedWaiverData(completedEventEntries);
        setIsLoading(false);
    }

    useEffect(() => {
        if (waiversData !== undefined) {
            setFormattedWaiverData(waiversData ?? []);
        }
    }, [waiversData]);

    useEffect(() => {
        if (completedEventEntries?.length && waiversData === undefined) {
            getDataOnLoad(completedEventEntries);
        }
    }, [completedEventEntries, waiversData]);

    /**
     * Imperative save called by the parent card's Save button. Replaces the previous
     * `continueClicks` counter pattern so the parent decides when to collapse and the
     * section just handles persistence.
     */
    useImperativeHandle(ref, () => ({
        save: async () => {
            if (hasUnsavedChanges) await handleSaveData();
        },
    }));

    const handleRuleInputChange = (entryIndex: number, ruleIndex: number, fullName: string, isChecked: boolean) => {
        if (entryIndex > -1) {
            const clickedEntry = formattedWaiverData[entryIndex];
            if (ruleIndex > -1 && clickedEntry.rules) {
                // Find the correct rule for this entry
                const clickedRule = clickedEntry.rules[ruleIndex];

                // Check if the rule agreement has been completed
                const isCompleted = verifyRuleInput(fullName, isChecked);

                // Update the rule with the new information
                const newFormattedEventRule: FormattedEventRule = {
                    ...clickedRule,
                    fullName: fullName,
                    isAgreed: isChecked
                };

                // Update the rules for this entry
                const newFormattedEventRules: FormattedEventRule[] = [
                    ...clickedEntry.rules.slice(0, ruleIndex),
                    newFormattedEventRule,
                    ...clickedEntry.rules.slice(ruleIndex+1)
                ];

                // Update the formatted entry data
                const newFormattedWaiverData: FormattedWaiverData = {
                    ...clickedEntry,
                    rules: newFormattedEventRules
                };
                const newFormattedWaiverDataItems: FormattedWaiverData[] = [
                    ...formattedWaiverData.slice(0, entryIndex),
                    newFormattedWaiverData,
                    ...formattedWaiverData.slice(entryIndex+1)
                ];
                setFormattedWaiverData(newFormattedWaiverDataItems);
                setHasUnsavedChanges(true);
                if (onItemsChange) onItemsChange(newFormattedWaiverDataItems);
            } else {
                setError("Sorry, a problem occurred while trying to find the rule.");
            }
        } else {
            setError("Sorry, a problem occurred while trying to find the entry.");
        }
    }

    const handleCollapseRule = (entryIndex: number, ruleIndex: number) => {
        if (entryIndex > -1) {
            const clickedEntry = formattedWaiverData[entryIndex];
            if (ruleIndex > -1 && clickedEntry.rules) {
                // Find the correct rule for this entry
                const clickedRule = clickedEntry.rules[ruleIndex];

                // Update the rule by switching the collapsed state
                const newFormattedEventRule: FormattedEventRule = {
                    ...clickedRule,
                    isCollapsed: !clickedRule.isCollapsed,
                };

                // Update the rules for this entry
                const newFormattedEventRules: FormattedEventRule[] = [
                    ...clickedEntry.rules.slice(0, ruleIndex),
                    newFormattedEventRule,
                    ...clickedEntry.rules.slice(ruleIndex+1)
                ];

                // Update the formatted entry data
                const newFormattedWaiverData: FormattedWaiverData = {
                    ...clickedEntry,
                    rules: newFormattedEventRules
                };
                const newFormattedWaiverDataItems: FormattedWaiverData[] = [
                    ...formattedWaiverData.slice(0, entryIndex),
                    newFormattedWaiverData,
                    ...formattedWaiverData.slice(entryIndex+1)
                ];
                setFormattedWaiverData(newFormattedWaiverDataItems);
                
            } else {
                setError("Sorry, a problem occurred while trying to find the rule.");
            }
        } else {
            setError("Sorry, a problem occurred while trying to find the entry.");
        }
    }

    const createRuleAgreement = async (eventEntry: EventEntry, formattedRule: FormattedEventRule) => {
        const input: CreateEventRulesAgreementInput = {
            eventId: event.id,
            eventRuleId: formattedRule.rule.id,
            eventEntryId: eventEntry.id,
            name: formattedRule.fullName,
            personId: user.id,
            personType: user.roles,
            createdBy: user.id,
            lastUpdatedBy: user.id
        };
        const createResult = await createEventRulesAgreement(input);
        return createResult;
    }

    const updateRuleAgreement = async (formattedRule: FormattedEventRule, pastRuleAgreement: EventRulesAgreement) => {
        const input: UpdateEventRulesAgreementInput = {
            id: pastRuleAgreement.id,
            name: formattedRule.fullName
        };
        const updateResult = await updateEventRulesAgreement(input);
        return updateResult;
    }

    const deleteRuleAgreement = async (pastRuleAgreement: EventRulesAgreement) => {
        const input: DeleteEventRulesAgreementInput = {
            id: pastRuleAgreement.id
        };
        const deleteResult = await deleteEventRulesAgreement(input);
        return deleteResult;
    }

    const handleSaveData = async() => {
        setIsLoading(true);

        let updatedFormattedData: FormattedWaiverData[] = [];

        for (let i = 0; i < formattedWaiverData.length; i++) {
            const formattedData: FormattedWaiverData = formattedWaiverData[i];
            const currentEntry = formattedData.eventEntry;
            const currentRules = formattedData.rules;

            const updatedRules: FormattedEventRule[] = [];

            for (let j = 0; j < currentRules.length; j++) {
                const currentRule = currentRules[j];

                // If the rule is not required, add the formatted data to the updated formatted data
                if (!currentRule.isRequired) {
                    updatedRules.push(currentRule);
                    continue;
                }

                // Verify that the rule agreement is valid
                const ruleAgreementIsValid = verifyRuleInput(currentRule.fullName, currentRule.isAgreed);

                if (!currentRule.pastRuleAgreement && ruleAgreementIsValid) {
                    const createResult = await createRuleAgreement(currentEntry, currentRule);
                    const newRuleAgreement = createResult.result;
                    if (createResult.isSuccess) {
                        const updatedCurrentRule = {
                            ...currentRule,
                            pastRuleAgreement: newRuleAgreement
                        };
                        updatedRules.push(updatedCurrentRule);
                    } else {
                        updatedRules.push(currentRule);
                    }
                } else if (currentRule.pastRuleAgreement && ruleAgreementIsValid) {
                    const updateResult = await updateRuleAgreement(currentRule, currentRule.pastRuleAgreement);
                    const newRuleAgreement = updateResult.result;
                    if (updateResult.isSuccess) {
                        const updatedCurrentRule = {
                            ...currentRule,
                            pastRuleAgreement: newRuleAgreement
                        };
                        updatedRules.push(updatedCurrentRule);
                    } else {
                        updatedRules.push(currentRule);
                    }
                } else if (currentRule.pastRuleAgreement && !ruleAgreementIsValid) {
                    const deleteResult = await deleteRuleAgreement(currentRule.pastRuleAgreement);
                    if (deleteResult.isSuccess) {
                        const updatedCurrentRule = {
                            ...currentRule,
                            pastRuleAgreement: null
                        };
                        updatedRules.push(updatedCurrentRule);
                    } else {
                        updatedRules.push(currentRule);
                    }
                } else {
                    updatedRules.push(currentRule);
                }
            }

            const updatedFormattedWaiverData: FormattedWaiverData = {
                ...formattedData,
                rules: updatedRules
            };
            updatedFormattedData.push(updatedFormattedWaiverData);
        }

        setFormattedWaiverData(updatedFormattedData);
        setHasUnsavedChanges(false);
        setIsLoading(false);
    }

    return (
        <>
            {error && <ErrorAlert width="12" error={error} />}
            {isLoading ? (
                <SubSectionCardsSkeleton
                    count={Math.max(completedEventEntries?.length ?? 0, 1)}
                    includeStablingNotes={false}
                />
            ) : (
                <>
                    {formattedWaiverData && formattedWaiverData.length > 0 ? (
                        <>
                            {formattedWaiverData?.map((data: FormattedWaiverData, index: number) => {
                                const onlyId = expandWaiverOnlyForEventEntryId;
                                const deepLinkMatchesSomeEntry =
                                    onlyId != null &&
                                    onlyId !== "" &&
                                    Boolean(
                                        formattedWaiverData?.some((d) => d.id === onlyId || d.eventEntry?.id === onlyId)
                                    );
                                const defaultExpandedForCard =
                                    onlyId != null && onlyId !== "" && deepLinkMatchesSomeEntry
                                        ? data.id === onlyId || data.eventEntry?.id === onlyId
                                        : false;
                                const entryHasIncompleteRules = hasIncompleteWaiversForEntry(data);
                                return (
                                    <EntrySubSectionCard
                                        key={data.id || `waiver-${index}`}
                                        remountKey={`${data.id}-${waiverAccordionFocusKey}`}
                                        entry={data.eventEntry}
                                        entryNumber={index + 1}
                                        defaultExpanded={defaultExpandedForCard}
                                        collapsedHeaderTrailing={
                                            <EntryStatusChip isComplete={!entryHasIncompleteRules} />
                                        }>
                                            {data.rules && data.rules.length > 0 ? (
                                                <div className="px-0">
                                                    {data.rules.map((formattedEventRule, ruleIndex) => (
                                                        <IonCard key={ruleIndex} className="mb-2 waiver-rule-card">
                                                            <IonCardContent className="px-2 py-2">
                                                                <IonRow
                                                                    className="ion-align-items-center ion-justify-content-between waiver-rule-card__header"
                                                                    onClick={() => handleCollapseRule(index, ruleIndex)}>
                                                                    <IonCol size="8" className="ion-no-padding">
                                                                        <IonLabel>
                                                                            <h2 className="font-weight-bold ion-text-wrap">
                                                                                {formattedEventRule.rule.title}
                                                                            </h2>
                                                                        </IonLabel>
                                                                    </IonCol>
                                                                    <IonCol size="4" className="ion-text-right">
                                                                        <IonChip 
                                                                            className={`m-0 rounded-2 text-right text-white font-weight-bold ${
                                                                                ((formattedEventRule.isRequired && verifyRuleInput(formattedEventRule.fullName, formattedEventRule.isAgreed)) || (!formattedEventRule.isRequired)) 
                                                                                    ? "bg-success" 
                                                                                    : "bg-warning"
                                                                            }`}
                                                                        >
                                                                            {((formattedEventRule.isRequired && verifyRuleInput(formattedEventRule.fullName, formattedEventRule.isAgreed)) || (!formattedEventRule.isRequired)) 
                                                                                ? "Complete" 
                                                                                : "Incomplete"
                                                                            }
                                                                        </IonChip>
                                                                    </IonCol>
                                                                </IonRow>

                                                                {!formattedEventRule.isCollapsed && (
                                                                    <>
                                                                        <IonRow>
                                                                            <IonCol className="p-0 pb-2">
                                                                                <IonCard className="mx-0 my-0 subsection-panel-card">
                                                                                    <IonCardContent className="px-3 py-2 bg-white">
                                                                                        <BasicTextarea
                                                                                            value={formattedEventRule.rule.text || ""}
                                                                                            rows={(isWindows() || isPlatform("desktop")) ? 5 : 15}
                                                                                            isReadOnly={true}
                                                                                        />
                                                                                    </IonCardContent>
                                                                                </IonCard>
                                                                            </IonCol>
                                                                        </IonRow>

                                                                        {formattedEventRule.rule.requiresAgreementPerEntry ? (
                                                                            <div className="mt-2">
                                                                                <NewRuleAgreementForm
                                                                                    initialFullName={formattedEventRule.fullName}
                                                                                    initialIsChecked={formattedEventRule.isAgreed}
                                                                                    onChange={(fullName: string, isChecked: boolean) => handleRuleInputChange(index, ruleIndex, fullName, isChecked)} 
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <IonRow className="mt-2">
                                                                                <IonCol className="px-0">
                                                                                    <IonText color="medium" className="waiver-rule-note--italic">
                                                                                        No digital signature is required for this rule.
                                                                                    </IonText>
                                                                                </IonCol>
                                                                            </IonRow>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </IonCardContent>
                                                        </IonCard>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="px-1 py-2">
                                                    <IonRow className="ion-justify-content-center">
                                                        <IonCol className="ion-text-center" sizeMd="10">
                                                            <IonText color="medium" className="waiver-rule-note">
                                                                This event does not have any rules specified.
                                                            </IonText>
                                                        </IonCol>
                                                    </IonRow>
                                                </div>
                                            )}
                                    </EntrySubSectionCard>
                                );
                            })}
                        </>
                    ) : (
                        <div className="px-1 py-2">
                            <IonRow className="ion-justify-content-center">
                                <IonCol className="ion-text-center">
                                    <IonText color="medium" className="waiver-rule-note">
                                        No entries found. Please go back up and add an entry.
                                    </IonText>
                                </IonCol>
                            </IonRow>
                        </div>
                    )}
                </>
            )}
        </>
    );
});

WaiversSection.displayName = "WaiversSection";

export default WaiversSection;
